import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchTaxTypeDetailsAsync } from "../../app/certificateSlice";
import { selectIsCompanyProvisioned, selectIsMultiTax } from "../../app/sessionSlice";
import { selectEnableMultiTax } from "../../app/settingsSlice";
import type { AppDispatch } from "../../app/types";

/**
 * Prefetches the tax-type-details tree at the app level so any page can read it
 * from Redux / sessionStorage without triggering its own API call.
 *
 * Gated on the same flags as the review-page `MultiTaxBlock` render
 * (`session.isMultiTax` + `enableMultiTax`) and on the active company being
 * provisioned. Settings load asynchronously, so the flags are effect
 * dependencies and the fetch fires as soon as they arrive.
 *
 * `fetchTaxTypeDetailsAsync` is idempotent and cache-aware (it short-circuits on
 * a matching Redux cache key or a sessionStorage hit), so running it centrally
 * is safe alongside `MultiTaxBlock`'s own lazy fetch — the block just finds the
 * cache already warm and skips the network call.
 */
export const usePrefetchTaxTypeDetails = (companyId: string | number | null | undefined): void => {
    const dispatch = useDispatch<AppDispatch>();
    const isProvisioned = useSelector(selectIsCompanyProvisioned);
    const isMultiTax = useSelector(selectIsMultiTax);
    const enableMultiTax = useSelector(selectEnableMultiTax);

    useEffect(() => {
        if (companyId && isProvisioned && isMultiTax && enableMultiTax) {
            dispatch(fetchTaxTypeDetailsAsync());
        }
    }, [dispatch, companyId, isProvisioned, isMultiTax, enableMultiTax]);
};
